import { LocationAsRelativeRaw, RouteParamsRaw } from 'vue-router';

import { UrlPathConstructor } from '../models/url';
import { Filter } from '../models/filter';
import { PaginationDefinition } from '../models/definition';
import { IRouteHandler } from './IRouteHandler';
import { router } from './RoutesIndex';
import { IQuery } from '../interfaces';
import store from '../store';
import { PortalType } from '@studyportals/domain-client';

declare const PORTALS_URL: Record<PortalType, string>;
/* eslint-disable @typescript-eslint/no-unsafe-member-access*/
export abstract class BaseRouteHandler implements IRouteHandler {
	private nextHandler?: IRouteHandler;
	private router = router;
	private urlPathConstructor = new UrlPathConstructor();

	public setNext(handler: IRouteHandler): void {
		this.nextHandler = handler;
	}

	public async next(filters: Filter[], urlQueryParam: IQuery, pageNumber: number): Promise<void> {
		if (this.nextHandler !== undefined) {
			await this.nextHandler.handle(filters, urlQueryParam, pageNumber);
		}
	}

	public async handle(filters: Filter[], query: IQuery, pageNumber: number): Promise<void> {
		if (this.canHandle(filters)) {
			const route = this.buildRoute(filters);

			this.addQuery(route, query);
			this.addPage(route, pageNumber);

			await this.router.push(route);
		} else {
			await this.next(filters, query, pageNumber);
		}
	}

	public async resolveToUrl(filters: Filter[], query: IQuery, pageNumber: number): Promise<string | undefined> {
		if (!this.canHandle(filters)) return this.nextHandler?.resolveToUrl(filters, query, pageNumber);
		const route = this.buildRoute(filters);
		this.addQuery(route, query);
		this.addPage(route, pageNumber);

		try {
			const resolvedRoute = await this.router.resolver(route);
			return `${this.currentPortalUrl}${resolvedRoute.href}`;
		} catch {
			return undefined;
		}
	}

	private get currentPortalUrl(): string {
		return PORTALS_URL[store.getters.getPortalType()];
	}

	public getPathParametersForFilters(filters: Filter[]): RouteParamsRaw {
		let parameters: RouteParamsRaw = {};
		if (this.canHandle(filters)) {
			const params = this.buildRoute(filters).params;
			if (params) {
				parameters = params;
			}
		}

		return parameters;
	}

	public abstract canHandle(filters: Filter[]): boolean;

	protected addQuery(route: any, query: IQuery): void {
		if (Object.keys(query).length > 0) {
			route['query'] = query;
		}
	}

	protected addPage(route: any, pageNumber: number): void {
		if (pageNumber === 2) {
			if (route['params'] !== undefined) {
				route['params']['page'] = PaginationDefinition.PAGE_TWO;

				return;
			}

			route['params'] = {
				page: PaginationDefinition.PAGE_TWO
			};
		}

		if (pageNumber > 2) {
			if (route['query'] !== undefined) {
				route['query']['page'] = pageNumber;

				return;
			}

			route['query'] = {
				page: pageNumber
			};
		}
	}

	protected extractPath(filter: Filter | undefined): string {
		if (filter !== undefined) {
			return this.urlPathConstructor.extractUrlPathPart(filter);
		}

		return '';
	}

	private buildRoute(filters: Filter[]): LocationAsRelativeRaw {
		const route = this.selectRoute(filters);
		route.params ??= {};
		route.params.portal_type = store.getters.getPortalDefinition().path;
		return route;
	}

	protected abstract selectRoute(filters: Filter[]): LocationAsRelativeRaw;
}
